'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import { actionVerbsByCategory, allActionVerbs } from '@/lib/tool-data/action-verbs';

type Tone = 'Professional' | 'Confident' | 'Executive';
type BulletCount = 3 | 5 | 8;

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function pickRandomVerb(usedVerbs: Set<string>): string {
  const available = allActionVerbs.filter((v) => !usedVerbs.has(v));
  if (available.length === 0) return pickRandom(allActionVerbs);
  const verb = pickRandom(available);
  usedVerbs.add(verb);
  return verb;
}

/** Split free-form text into distinct responsibility phrases. */
function parseResponsibilities(text: string): string[] {
  // Split on newlines, bullets, semicolons, and numbered lists
  const raw = text
    .split(/[\n\r]+|[;]|(?:^|\s)[-*•]\s|(?:^|\s)\d+[.)]\s/)
    .map((s) => s.trim().replace(/^[,.\s]+|[,.\s]+$/g, ''))
    .filter((s) => s.length > 5);

  if (raw.length === 0) {
    // Fallback: split on commas or " and "
    return text
      .split(/,\s*|\s+and\s+/)
      .map((s) => s.trim().replace(/^[,.\s]+|[,.\s]+$/g, ''))
      .filter((s) => s.length > 5);
  }
  return raw;
}

/** Normalise a phrase to start lowercase (unless acronym). */
function lowerFirst(str: string): string {
  if (!str) return str;
  if (str.length > 1 && str[0] === str[0].toUpperCase() && str[1] === str[1].toLowerCase()) {
    return str[0].toLowerCase() + str.slice(1);
  }
  return str;
}

/** Generate a plausible metric snippet suited to the phrase. */
function generateMetric(phrase: string, tone: Tone): string {
  const metricsPool = [
    'increasing efficiency by 25%',
    'reducing costs by 18%',
    'improving team productivity by 30%',
    'boosting revenue by $150K annually',
    'cutting turnaround time by 40%',
    'growing customer satisfaction scores by 20%',
    'saving 15+ hours per week',
    'driving a 35% improvement in key metrics',
    'achieving 98% on-time delivery rate',
    'expanding market reach by 22%',
    'decreasing error rates by 45%',
    'accelerating project timelines by 3 weeks',
    'supporting 50+ stakeholders across departments',
    'managing a portfolio valued at $2M+',
    'onboarding 12 new team members',
    'handling 200+ client accounts simultaneously',
    'generating 40% more qualified leads',
    'reducing customer churn by 15%',
    'increasing NPS score from 42 to 67',
    'delivering $500K in annual cost savings',
  ];

  const executiveMetrics = [
    'delivering $1.2M in incremental revenue',
    'achieving 150% of annual targets',
    'driving a 28% YoY growth trajectory',
    'overseeing a $5M operational budget',
    'impacting 10K+ end users',
    'resulting in a 3x ROI within 12 months',
  ];

  if (tone === 'Executive') {
    return pickRandom([...metricsPool, ...executiveMetrics]);
  }
  return pickRandom(metricsPool);
}

/* ------------------------------------------------------------------ */
/*  Template-based bullet generator                                   */
/* ------------------------------------------------------------------ */

function generateBullets(
  responsibilities: string[],
  jobTitle: string,
  company: string,
  count: BulletCount,
  tone: Tone,
): string[] {
  const usedVerbs = new Set<string>();
  const bullets: string[] = [];

  // Build phrase pool – cycle through if fewer phrases than count
  const phrases =
    responsibilities.length > 0 ? responsibilities : ['key initiatives and projects'];

  const toneAdverbs: Record<Tone, string[]> = {
    Professional: ['effectively', 'successfully', 'consistently', 'proactively'],
    Confident: ['strategically', 'decisively', 'expertly', 'skillfully'],
    Executive: ['enterprise-wide', 'cross-functionally', 'at scale', 'organization-wide'],
  };

  const templates = [
    // Template 1: Verb + phrase + metric
    (verb: string, phrase: string) => {
      const metric = generateMetric(phrase, tone);
      return `${verb} ${lowerFirst(phrase)}, ${metric}.`;
    },
    // Template 2: Verb + adverb + phrase + "resulting in" + metric
    (verb: string, phrase: string) => {
      const adverb = pickRandom(toneAdverbs[tone]);
      const metric = generateMetric(phrase, tone);
      return `${verb} ${adverb} ${lowerFirst(phrase)}, resulting in ${metric}.`;
    },
    // Template 3: Verb + phrase + "for" context + metric
    (verb: string, phrase: string) => {
      const context = company
        ? `for ${company}`
        : jobTitle
          ? `as ${jobTitle}`
          : 'for the organization';
      const metric = generateMetric(phrase, tone);
      return `${verb} ${lowerFirst(phrase)} ${context}, ${metric}.`;
    },
    // Template 4: Verb + phrase + "which led to" + metric
    (verb: string, phrase: string) => {
      const metric = generateMetric(phrase, tone);
      return `${verb} ${lowerFirst(phrase)}, which led to ${metric}.`;
    },
    // Template 5: Verb + phrase + "to achieve" + metric
    (verb: string, phrase: string) => {
      const metric = generateMetric(phrase, tone);
      return `${verb} ${lowerFirst(phrase)} to achieve ${metric}.`;
    },
  ];

  for (let i = 0; i < count; i++) {
    const phrase = phrases[i % phrases.length];
    const verb = pickRandomVerb(usedVerbs);
    const template = templates[i % templates.length];
    bullets.push(template(verb, phrase));
  }

  return bullets;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function BulletGeneratorContent() {
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [bulletCount, setBulletCount] = useState<BulletCount>(5);
  const [tone, setTone] = useState<Tone>('Professional');
  const [bullets, setBullets] = useState<string[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleGenerate = () => {
    const parsed = parseResponsibilities(responsibilities);
    const result = generateBullets(parsed, jobTitle, company, bulletCount, tone);
    setBullets(result);
    setHasGenerated(true);
  };

  const allBulletsText = bullets.map((b) => `• ${b}`).join('\n');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resume Bullet Point Generator"
        description="Generate powerful, achievement-focused resume bullet points with strong action verbs and quantified results."
        category="Resume"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* ── Input Form ── */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Describe Your Experience</h2>

          {/* Job Title */}
          <div className="mb-5">
            <label htmlFor="jobTitle" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Job Title
            </label>
            <input
              id="jobTitle"
              type="text"
              placeholder="e.g. Senior Marketing Manager"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            />
          </div>

          {/* Company / Industry */}
          <div className="mb-5">
            <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Company / Industry <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="company"
              type="text"
              placeholder="e.g. SaaS / Fintech startup"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            />
          </div>

          {/* Responsibilities textarea */}
          <div className="mb-6">
            <label
              htmlFor="responsibilities"
              className="block text-sm font-semibold text-gray-700 mb-1.5"
            >
              Describe your responsibilities and achievements
            </label>
            <textarea
              id="responsibilities"
              rows={5}
              placeholder={`e.g.\n- Managed a team of 8 designers and developers\n- Launched email campaigns that grew subscriber base\n- Partnered with sales to create pitch decks and case studies\n- Reduced onboarding time for new hires`}
              value={responsibilities}
              onChange={(e) => setResponsibilities(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-y"
            />
          </div>

          {/* Options row */}
          <div className="flex flex-col sm:flex-row gap-6 mb-8">
            {/* Number of bullets */}
            <div>
              <span className="block text-sm font-semibold text-gray-700 mb-2">
                Number of bullets
              </span>
              <div className="flex gap-2">
                {([3, 5, 8] as BulletCount[]).map((n) => (
                  <button
                    key={n}
                    onClick={() => setBulletCount(n)}
                    className={`px-5 py-2 rounded-xl text-sm font-bold transition-colors ${
                      bulletCount === n
                        ? 'bg-blue-600 text-white shadow'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div>
              <label htmlFor="tone" className="block text-sm font-semibold text-gray-700 mb-2">
                Tone
              </label>
              <select
                id="tone"
                value={tone}
                onChange={(e) => setTone(e.target.value as Tone)}
                className="border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm"
              >
                <option value="Professional">Professional</option>
                <option value="Confident">Confident</option>
                <option value="Executive">Executive</option>
              </select>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!responsibilities.trim()}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-8 rounded-xl shadow text-lg transition"
          >
            Generate Bullets
          </button>
        </div>

        {/* ── Results ── */}
        {hasGenerated && bullets.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Your Resume Bullets</h2>
              <CopyButton text={allBulletsText} label="Copy All" />
            </div>

            <div className="space-y-4">
              {bullets.map((bullet, idx) => {
                // Bold the action verb (first word)
                const spaceIdx = bullet.indexOf(' ');
                const verb = bullet.slice(0, spaceIdx);
                const rest = bullet.slice(spaceIdx);

                return (
                  <div
                    key={idx}
                    className="flex items-start gap-3 bg-gray-50 rounded-xl border border-gray-100 p-4"
                  >
                    <span className="text-blue-500 font-bold mt-0.5 select-none">&#8226;</span>
                    <p className="flex-1 text-gray-800 leading-relaxed">
                      <strong className="text-gray-900">{verb}</strong>
                      {rest}
                    </p>
                    <CopyButton text={bullet} label="Copy" className="shrink-0" />
                  </div>
                );
              })}
            </div>

            {/* Re-generate hint */}
            <p className="text-sm text-gray-500 mt-6 text-center">
              Not quite right? Click <strong>Generate Bullets</strong> again for fresh variations.
            </p>
          </div>
        )}

        {/* ── Tips Callout ── */}
        {hasGenerated && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-10">
            <h3 className="text-lg font-bold text-blue-900 mb-3">
              The Winning Bullet Point Formula
            </h3>
            <p className="text-blue-800 mb-4 leading-relaxed">
              Great resume bullets follow a proven structure. Use this formula every time:
            </p>
            <div className="bg-white rounded-xl p-4 text-center font-mono text-sm sm:text-base text-blue-700 border border-blue-200">
              <span className="font-bold">Action Verb</span> +{' '}
              <span className="font-bold">Task</span> +{' '}
              <span className="font-bold">Metric</span> +{' '}
              <span className="font-bold">Result</span>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-blue-800">
              <li>
                <strong>Action Verb</strong> &mdash; Start every bullet with a powerful past-tense
                verb (e.g. Spearheaded, Engineered, Delivered).
              </li>
              <li>
                <strong>Task</strong> &mdash; Briefly describe what you did or owned.
              </li>
              <li>
                <strong>Metric</strong> &mdash; Add a number: dollars, percentages, counts,
                or timeframes.
              </li>
              <li>
                <strong>Result</strong> &mdash; Connect the metric to a business outcome
                (revenue, efficiency, satisfaction).
              </li>
            </ul>
          </div>
        )}

        {/* ── Educational Section ── */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Write Powerful Resume Bullet Points</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Resume bullet points are the single most important element of your resume. Recruiters spend an average of 6-7 seconds scanning a resume, and strong bullet points are what make them stop and read more closely. The best bullets combine a strong action verb, a specific task or project, and a measurable result that demonstrates your impact.
            </p>
            <p>
              Many job seekers make the mistake of listing responsibilities instead of achievements. Saying &quot;Responsible for managing a team&quot; tells a recruiter nothing about how well you did the job. Instead, framing it as &quot;Led a team of 12 engineers to deliver a $2M platform migration 3 weeks ahead of schedule&quot; paints a vivid picture of your capabilities and results.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">Tips for Better Bullet Points</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Start every bullet with a strong action verb</strong> -- words like &quot;Spearheaded,&quot; &quot;Delivered,&quot; &quot;Optimized,&quot; and &quot;Engineered&quot; convey confidence and ownership.
              </li>
              <li>
                <strong>Quantify your achievements</strong> -- include numbers, percentages, dollar amounts, or timeframes wherever possible to make your impact concrete.
              </li>
              <li>
                <strong>Focus on outcomes, not duties</strong> -- answer &quot;so what?&quot; for every bullet. How did your work benefit the team, company, or customers?
              </li>
              <li>
                <strong>Tailor bullets to the job description</strong> -- mirror the language and priorities of the role you are applying for to improve ATS matching and recruiter relevance.
              </li>
              <li>
                <strong>Keep bullets concise</strong> -- aim for one to two lines each. Dense paragraphs get skipped during resume scans.
              </li>
            </ul>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/resume-bullet-points" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Write Resume Bullet Points</a>
              {' · '}
              <a href="https://hbr.org/2014/12/how-to-write-a-resume-that-stands-out" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">HBR: How to Write a Resume That Stands Out</a>
              {' · '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics: Occupational Outlook Handbook</a>
            </p>
          </div>
        </section>

        {/* ── CTA ── */}
        <ToolCTA
          headline="Want bullets from YOUR actual career history?"
          description="Superpower Resume analyzes your real experience and generates tailored, metrics-driven bullet points for every role on your resume."
          buttonText="Generate Personalized Bullets Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
